/**
 * ChatCharts.js — v2.0
 * Smart chart detection — only shows chart when data is meaningful
 */

import { useState } from 'react'
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer
} from 'recharts'

const COLORS = ['#f97316','#3b82f6','#10b981','#a855f7','#ef4444','#f59e0b','#06b6d4','#ec4899']

// ── Parse number from strings like "$1,353.92K" "32.28%" "182" ───────────────
function parseNum(str) {
  if (!str) return null
  const s = String(str).replace(/[,$%✅❌~*()]/g, '').trim()
  // Skip pure text, dashes, arrows
  if (s.match(/^[a-zA-Z\s\-—→]+$/)) return null
  const m = s.match(/^([+-]?\d[\d.]*)([KMB]?)/)
  if (!m) return null
  let v = parseFloat(m[1])
  if (isNaN(v)) return null
  if (m[2] === 'K') v *= 1000
  if (m[2] === 'M') v *= 1_000_000
  if (m[2] === 'B') v *= 1_000_000_000
  return v
}

// ── Parse markdown tables from answer ────────────────────────────────────────
function parseTables(answer) {
  const tables = []
  const lines = answer.split('\n')
  let i = 0
  while (i < lines.length) {
    const line = lines[i].trim()
    if (line.startsWith('|') && i + 1 < lines.length && lines[i+1].trim().match(/^\|[-| :]+\|/)) {
      const headers = line.split('|').map(c => c.trim()).filter(Boolean)
      i += 2
      const rows = []
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        const cells = lines[i].trim().split('|').map(c => c.trim()).filter(Boolean)
        if (cells.length >= 2) {
          const row = {}
          headers.forEach((h, idx) => { row[h] = cells[idx] || '' })
          rows.push(row)
        }
        i++
      }
      if (rows.length > 0) tables.push({ headers, rows })
    } else { i++ }
  }
  return tables
}

// ── Check if a value is a meaningful financial amount (not headcount/ratio) ──
function isFinancialValue(v) {
  return v != null && Math.abs(v) >= 1000 // at least $1K
}

// ── Check if value is a count (headcount) ────────────────────────────────────
function isHeadcount(v) {
  return v != null && v >= 50 && v <= 10000 && Number.isInteger(v)
}

// ── Check if value is a ratio/percentage ─────────────────────────────────────
function isRatio(v) {
  return v != null && Math.abs(v) < 50
}

// ═════════════════════════════════════════════════════════════════════════════
// MAIN DETECTION ENGINE
// ═════════════════════════════════════════════════════════════════════════════
export function detectChart(question, apiResponse) {
  if (!apiResponse) return null
  const q = question.toLowerCase()
  const answer = apiResponse.answer || ''
  const tables = parseTables(answer)

  // ── RULE: Single value questions → NEVER show chart ──
  const singleValuePatterns = [
    /^what (is|was) (the )?(gross revenue|net revenue|opex|ebita|headcount|revenue|cogs)/,
    /^(what|how much) (is|was) .{0,30}\?$/,
    /^(show|get|give) me (the )?(latest|current) (period|month|date)/,
    /^what (period|month|data)/,
  ]
  if (singleValuePatterns.some(p => p.test(q))) return null

  // ── RULE: Comparison question → Grouped Bar (only with $K+ values) ──
  if (q.includes('compare') || q.includes(' vs ') || q.includes('versus') ||
      q.includes('difference') || q.includes('mom') || q.includes('last 2 months') ||
      q.includes('compare last')) {
    return buildComparisonChart(tables, answer, q)
  }

  // ── RULE: OPEX breakdown → Donut (only departments with $K values) ──
  if (q.includes('opex breakdown') || q.includes('opex by') ||
      (q.includes('breakdown') && q.includes('opex'))) {
    return buildOpexDonut(tables, answer)
  }

  // ── RULE: Revenue breakdown → Donut ──
  if (q.includes('revenue breakdown') || q.includes('revenue by') ||
      (q.includes('breakdown') && q.includes('revenue'))) {
    return buildRevenueDonut(tables, answer)
  }

  // ── RULE: Headcount by department → Horizontal Bar (count values) ──
  if (q.includes('headcount by') || q.includes('headcount breakdown') ||
      (q.includes('department') && q.includes('headcount'))) {
    return buildHeadcountBar(tables, answer)
  }

  // ── RULE: KPI ratios → KPI Chart ──
  if (q.includes('kpi') || q.includes('ratios') ||
      (q.includes('ratio') && !q.includes('trend'))) {
    return buildKpiChart(tables, answer)
  }

  // ── RULE: Trend questions → Line Chart (only if multiple time periods) ──
  if (q.includes('trend') || q.includes('over time') ||
      q.includes('last 6 month') || q.includes('last 3 month') ||
      q.includes('history')) {
    return buildTrendChart(tables, answer, q)
  }

  // ── RULE: Summary/Overview → Bar Chart (only key financials) ──
  if (q.includes('summarize') || q.includes('summary') ||
      q.includes('overview') || q.includes('full p&l') ||
      q.includes('p&l for') || q.includes('latest month')) {
    return buildSummaryBar(tables, answer)
  }

  return null
}

// ── BUILD COMPARISON CHART ────────────────────────────────────────────────────
function buildComparisonChart(tables, answer, q) {
  // Extract period labels from answer heading
  let p1 = 'Period 1', p2 = 'Period 2'
  const m = answer.match(/##\s*([A-Z]{3}\s+\d{4})\s+vs\s+([A-Z]{3}\s+\d{4})/i)
  if (m) { p1 = m[1]; p2 = m[2] }
  else {
    for (const t of tables) {
      const h = t.headers
      if (h.length >= 3) {
        const hm1 = h[1]?.match(/([A-Z]{3}\s+\d{4})/i)
        const hm2 = h[2]?.match(/([A-Z]{3}\s+\d{4})/i)
        if (hm1 && hm2) { p1 = hm1[1]; p2 = hm2[1]; break }
      }
    }
  }

  // Only include KEY financial metrics with large enough values
  const keyMetrics = [
    'gross revenue', 'net revenue', 'total opex', 'ebita',
    'ps revenue', 'maintenance revenue', 'professional services revenue'
  ]

  const data = []
  for (const table of tables) {
    const h = table.headers
    if (h.length < 3) continue
    for (const row of table.rows) {
      const label = (row[h[0]] || '').replace(/[✅❌*~]/g, '').trim()
      const isKey = keyMetrics.some(k => label.toLowerCase().includes(k))
      if (!isKey) continue
      const v1 = parseNum(row[h[1]])
      const v2 = parseNum(row[h[2]])
      if (!isFinancialValue(v1) || !isFinancialValue(v2)) continue
      if (data.find(d => d.name === label.slice(0, 16))) continue
      data.push({
        name: label.slice(0, 16),
        [p1]: Math.round(v1 / 1000),
        [p2]: Math.round(v2 / 1000),
      })
    }
  }

  if (data.length < 2) return null
  return { type: 'groupedBar', title: `${p1} vs ${p2}`, data: data.slice(0, 6), keys: [p1, p2], fmt: v => `$${v}K` }
}

// ── BUILD OPEX DONUT ──────────────────────────────────────────────────────────
function buildOpexDonut(tables, answer) {
  const depts = ['professional services', 'general & administration', 'research & development',
                 'maintenance', 'sales & marketing', 'g&a', 'r&d', 'ps', 'ma', 'sm', 'ga']

  const data = []
  for (const table of tables) {
    const h = table.headers
    if (h.length < 2) continue
    // Find value column (first numeric col after label)
    const valCol = h.find((c, i) => i > 0 && !c.toLowerCase().includes('change') &&
                                    !c.toLowerCase().includes('%') &&
                                    !c.toLowerCase().includes('target') &&
                                    !c.toLowerCase().includes('status'))
    if (!valCol) continue

    for (const row of table.rows) {
      const label = (row[h[0]] || '').toLowerCase().replace(/[✅❌*~]/g, '').trim()
      const isDept = depts.some(d => label.includes(d))
      if (!isDept) continue
      const v = parseNum(row[valCol])
      if (!isFinancialValue(v)) continue
      const cleanLabel = (row[h[0]] || '').replace(/[✅❌*~]/g, '').trim()
      if (!data.find(d => d.name === cleanLabel)) {
        data.push({ name: cleanLabel.slice(0, 22), value: Math.abs(Math.round(v / 1000)) })
      }
    }
  }

  if (data.length < 2) return null
  return { type: 'donut', title: 'OPEX by Department ($K)', data: data.slice(0, 8), fmt: v => `$${v}K` }
}

// ── BUILD REVENUE DONUT ───────────────────────────────────────────────────────
function buildRevenueDonut(tables, answer) {
  const revItems = ['professional services', 'maintenance', 'license', 'hardware',
                    'flowthrough', 'intercompany', 'ps revenue', 'recurring']
  const data = []
  for (const table of tables) {
    const h = table.headers
    if (h.length < 2) continue
    const valCol = h.find((c, i) => i > 0 && !c.toLowerCase().includes('change') &&
                                    !c.toLowerCase().includes('%'))
    if (!valCol) continue
    for (const row of table.rows) {
      const label = (row[h[0]] || '').toLowerCase()
      const isRev = revItems.some(r => label.includes(r))
      if (!isRev) continue
      const v = parseNum(row[valCol])
      if (!isFinancialValue(v)) continue
      const cleanLabel = (row[h[0]] || '').replace(/[✅❌*~]/g, '').trim()
      if (!data.find(d => d.name === cleanLabel)) {
        data.push({ name: cleanLabel.slice(0, 22), value: Math.abs(Math.round(v / 1000)) })
      }
    }
  }
  if (data.length < 2) return null
  return { type: 'donut', title: 'Revenue Breakdown ($K)', data: data.slice(0, 8), fmt: v => `$${v}K` }
}

// ── BUILD HEADCOUNT BAR ───────────────────────────────────────────────────────
function buildHeadcountBar(tables, answer) {
  const data = []
  for (const table of tables) {
    const h = table.headers
    if (h.length < 2) continue
    const valCol = h.find((c, i) => i > 0 && !c.toLowerCase().includes('change'))
    if (!valCol) continue
    for (const row of table.rows) {
      const label = (row[h[0]] || '').replace(/[✅❌*~]/g, '').trim()
      if (!label || label.toLowerCase().includes('total')) continue
      const v = parseNum(row[valCol])
      if (v == null || v < 1 || v > 5000) continue
      if (!data.find(d => d.name === label)) {
        data.push({ name: label.slice(0, 22), value: Math.round(v) })
      }
    }
  }
  if (data.length < 2) return null
  return { type: 'hbar', title: 'Headcount by Department', data: data.slice(0, 8), fmt: v => `${v}` }
}

// ── BUILD KPI CHART ───────────────────────────────────────────────────────────
function buildKpiChart(tables, answer) {
  const kpiDefs = {
    'professional services': { target: 2, lower: false, label: 'PS Ratio' },
    'maintenance':           { target: 4, lower: false, label: 'MA Ratio' },
    'r&d':                   { target: 1, lower: false, label: 'R&D Ratio' },
    'sales & marketing':     { target: 1.5, lower: false, label: 'SM Ratio' },
    'administration':        { target: 15, lower: true, label: 'Admin %' },
    'ebita %':               { target: 20, lower: false, label: 'EBITA %' },
  }

  const data = []
  for (const table of tables) {
    const h = table.headers
    if (h.length < 2) continue
    const valCol = h.find((c, i) => i > 0 && !c.toLowerCase().includes('target') &&
                                    !c.toLowerCase().includes('status') &&
                                    !c.toLowerCase().includes('change'))
    if (!valCol) continue

    for (const row of table.rows) {
      const rawLabel = (row[h[0]] || '').replace(/[✅❌()><%]/g, '').trim()
      const labelLower = rawLabel.toLowerCase()
      const kpiKey = Object.keys(kpiDefs).find(k => labelLower.includes(k))
      if (!kpiKey) continue
      const v = parseNum(row[valCol])
      if (v == null) continue
      const { target, lower, label } = kpiDefs[kpiKey]
      const pass = lower ? v < target : v > target
      if (!data.find(d => d.name === label)) {
        data.push({ name: label, value: parseFloat(v.toFixed(2)), target, pass })
      }
    }
  }

  if (data.length < 2) return null
  return { type: 'kpiBar', title: 'KPI vs Targets', data, fmt: v => `${v}` }
}

// ── BUILD TREND CHART ─────────────────────────────────────────────────────────
function buildTrendChart(tables, answer, q) {
  // Only build trend if we have time-series data (multiple period rows)
  // Look for table with period/month labels in first column
  for (const table of tables) {
    const h = table.headers
    if (h.length < 2) return null

    // Check first column has period-like labels
    const firstColVals = table.rows.map(r => r[h[0]] || '')
    const hasPeriods = firstColVals.some(v =>
      /^(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)\s+\d{4}$/i.test(v.trim()) ||
      /^\d{4}-(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)$/i.test(v.trim())
    )

    if (!hasPeriods) continue
    if (table.rows.length < 3) continue // need at least 3 data points

    // Find value columns with meaningful data
    const valCols = h.slice(1).filter(col => {
      const vals = table.rows.map(r => parseNum(r[col])).filter(v => v != null)
      if (vals.length < 3) return false
      // Check if values are headcount (50-5000) or financial ($K+)
      const avgVal = vals.reduce((a, b) => a + b, 0) / vals.length
      return avgVal > 0
    })

    if (valCols.length === 0) continue

    const isHCTrend = q.includes('headcount')
    const data = table.rows.map(row => {
      const point = { name: (row[h[0]] || '').trim() }
      valCols.forEach(col => {
        const v = parseNum(row[col])
        if (v != null) {
          point[col] = isHCTrend ? Math.round(v) : Math.round(v / 1000)
        }
      })
      return point
    }).filter(d => d.name && Object.keys(d).length > 1)

    if (data.length < 3) continue

    return {
      type: 'line',
      title: isHCTrend ? 'Headcount Trend' : 'Trend Analysis',
      data,
      keys: valCols,
      fmt: isHCTrend ? v => `${v}` : v => `$${v}K`
    }
  }
  return null
}

// ── BUILD SUMMARY BAR ─────────────────────────────────────────────────────────
function buildSummaryBar(tables, answer) {
  const keyMetrics = ['gross revenue', 'net revenue', 'total opex', 'ebita']
  const data = []

  for (const table of tables) {
    const h = table.headers
    if (h.length < 2) continue
    const valCol = h.find((c, i) => i > 0 &&
                                    !c.toLowerCase().includes('target') &&
                                    !c.toLowerCase().includes('status'))
    if (!valCol) continue

    for (const row of table.rows) {
      const label = (row[h[0]] || '').replace(/[✅❌*~]/g, '').trim()
      const isKey = keyMetrics.some(k => label.toLowerCase().includes(k))
      if (!isKey) continue
      const v = parseNum(row[valCol])
      if (!isFinancialValue(v)) continue
      if (data.find(d => d.name === label.slice(0, 16))) continue
      data.push({ name: label.slice(0, 16), value: Math.round(Math.abs(v) / 1000) })
    }
  }

  if (data.length < 2) return null
  return { type: 'bar', title: 'P&L Summary ($K)', data, fmt: v => `$${v}K` }
}

// ── CHART TYPES for switcher — only show relevant ones ───────────────────────
function getRelevantTypes(chartType) {
  switch(chartType) {
    case 'groupedBar': return [
      { id: 'groupedBar', icon: '📊', label: 'Compare' },
      { id: 'bar',        icon: '📈', label: 'Bar' },
      { id: 'line',       icon: '📉', label: 'Line' },
    ]
    case 'donut': return [
      { id: 'donut', icon: '🍩', label: 'Donut' },
      { id: 'bar',   icon: '📊', label: 'Bar' },
    ]
    case 'kpiBar': return [
      { id: 'kpiBar', icon: '🎯', label: 'KPI' },
      { id: 'bar',    icon: '📊', label: 'Bar' },
    ]
    case 'hbar': return [
      { id: 'hbar', icon: '📊', label: 'Horizontal' },
      { id: 'donut', icon: '🍩', label: 'Donut' },
    ]
    case 'line': return [
      { id: 'line', icon: '📉', label: 'Line' },
      { id: 'bar',  icon: '📊', label: 'Bar' },
    ]
    default: return [
      { id: 'bar',   icon: '📊', label: 'Bar' },
      { id: 'donut', icon: '🍩', label: 'Donut' },
      { id: 'line',  icon: '📉', label: 'Line' },
    ]
  }
}

// ── MAIN CHART COMPONENT ──────────────────────────────────────────────────────
export function InlineChart({ chartConfig, large }) {
  const [open, setOpen]         = useState(false)
  const [activeType, setActiveType] = useState(chartConfig?.type || 'bar')

  if (!chartConfig || !chartConfig.data || chartConfig.data.length === 0) return null

  const h   = large ? 200 : 160
  const fs  = large ? 12 : 11
  const relevantTypes = getRelevantTypes(chartConfig.type)

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null
    return (
      <div style={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '10px 14px' }}>
        <p style={{ margin: '0 0 6px', fontSize: 11, color: '#94a3b8', fontWeight: 700 }}>{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ margin: '2px 0', fontSize: 12, color: p.color || '#fff', fontWeight: 700 }}>
            {p.name}: {chartConfig.fmt ? chartConfig.fmt(p.value) : p.value}
          </p>
        ))}
      </div>
    )
  }

  const renderChart = () => {
    const { data, keys } = chartConfig

    if (activeType === 'groupedBar') {
      const barKeys = keys || Object.keys(data[0]).filter(k => k !== 'name')
      return (
        <ResponsiveContainer width="100%" height={h}>
          <BarChart data={data} margin={{ top: 8, right: 10, left: 0, bottom: 40 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
            <XAxis dataKey="name" tick={{ fontSize: fs - 1, fill: '#64748b' }} angle={-15} textAnchor="end" interval={0} />
            <YAxis tickFormatter={v => `$${v}K`} tick={{ fontSize: fs, fill: '#64748b' }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: fs }} />
            {barKeys.map((k, i) => <Bar key={k} dataKey={k} fill={COLORS[i % COLORS.length]} radius={[4, 4, 0, 0]} maxBarSize={40} />)}
          </BarChart>
        </ResponsiveContainer>
      )
    }

    if (activeType === 'line') {
      const lineKeys = keys || Object.keys(data[0]).filter(k => k !== 'name')
      return (
        <ResponsiveContainer width="100%" height={h}>
          <LineChart data={data} margin={{ top: 8, right: 20, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
            <XAxis dataKey="name" tick={{ fontSize: fs - 1, fill: '#64748b' }} angle={-15} textAnchor="end" />
            <YAxis tick={{ fontSize: fs, fill: '#64748b' }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: fs }} />
            {lineKeys.map((k, i) => <Line key={k} type="monotone" dataKey={k} stroke={COLORS[i % COLORS.length]} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />)}
          </LineChart>
        </ResponsiveContainer>
      )
    }

    if (activeType === 'donut') {
      const RADIAN = Math.PI / 180
      const renderLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
        if (percent < 0.06) return null
        const r = innerRadius + (outerRadius - innerRadius) * 0.5
        const x = cx + r * Math.cos(-midAngle * RADIAN)
        const y = cy + r * Math.sin(-midAngle * RADIAN)
        return <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={10} fontWeight={700}>{`${(percent*100).toFixed(0)}%`}</text>
      }
      return (
        <ResponsiveContainer width="100%" height={h}>
          <PieChart>
            <Pie data={data} cx="50%" cy="50%" innerRadius={h * 0.22} outerRadius={h * 0.38} dataKey="value" labelLine={false} label={renderLabel}>
              {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: fs }} />
          </PieChart>
        </ResponsiveContainer>
      )
    }

    if (activeType === 'kpiBar') {
      return (
        <ResponsiveContainer width="100%" height={Math.max(h, data.length * 34)}>
          <BarChart data={data} layout="vertical" margin={{ top: 5, right: 40, left: 100, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: fs, fill: '#64748b' }} />
            <YAxis dataKey="name" type="category" tick={{ fontSize: fs - 1, fill: '#64748b' }} width={100} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: fs }} />
            <Bar dataKey="value" name="Actual" radius={[0, 4, 4, 0]} maxBarSize={18}>
              {data.map((entry, i) => <Cell key={i} fill={entry.pass ? '#22c55e' : '#ef4444'} />)}
            </Bar>
            <Bar dataKey="target" name="Target" fill="rgba(0,0,0,0.15)" radius={[0, 4, 4, 0]} maxBarSize={18} />
          </BarChart>
        </ResponsiveContainer>
      )
    }

    if (activeType === 'hbar') {
      return (
        <ResponsiveContainer width="100%" height={Math.max(h, data.length * 34)}>
          <BarChart data={data} layout="vertical" margin={{ top: 5, right: 30, left: 110, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: fs, fill: '#64748b' }} />
            <YAxis dataKey="name" type="category" tick={{ fontSize: fs - 1, fill: '#64748b' }} width={110} />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="value" name="Headcount" radius={[0, 4, 4, 0]} maxBarSize={18}>
              {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )
    }

    // Default Bar
    return (
      <ResponsiveContainer width="100%" height={h}>
        <BarChart data={data} margin={{ top: 8, right: 10, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
          <XAxis dataKey="name" tick={{ fontSize: fs, fill: '#64748b' }} />
          <YAxis tick={{ fontSize: fs, fill: '#64748b' }} />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="value" radius={[4, 4, 0, 0]} maxBarSize={50}>
            {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    )
  }

  const chartIcon = chartConfig.type === 'line' ? '📉' :
                    chartConfig.type === 'donut' ? '🍩' :
                    chartConfig.type === 'kpiBar' ? '🎯' :
                    chartConfig.type === 'hbar' ? '📊' : '📊'

  return (
    <div style={{ marginTop: 12 }}>
      {!open && (
        <button onClick={() => setOpen(true)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '7px 16px', background: 'linear-gradient(135deg,#fff7ed,#ffedd5)', border: '1.5px solid #fed7aa', borderRadius: 10, cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#ea580c', transition: 'all 0.18s' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = '#f97316'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(249,115,22,0.2)' }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = '#fed7aa'; e.currentTarget.style.boxShadow = 'none' }}>
          <span style={{ fontSize: 16 }}>{chartIcon}</span>
          View Chart — {chartConfig.title}
          <span style={{ fontSize: 10 }}>▼</span>
        </button>
      )}

      {open && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', background: 'linear-gradient(135deg,#fff7ed,#ffedd5)', borderBottom: '1px solid #fed7aa' }}>
            <span style={{ fontSize: 13, fontWeight: 800, color: '#ea580c' }}>{chartIcon} {chartConfig.title}</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              {/* Only show relevant chart type buttons */}
              <div style={{ display: 'flex', gap: 3, background: 'rgba(255,255,255,0.7)', borderRadius: 8, padding: 3 }}>
                {relevantTypes.map(ct => (
                  <button key={ct.id} onClick={() => setActiveType(ct.id)} title={ct.label}
                    style={{ width: 30, height: 26, borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', background: activeType === ct.id ? '#f97316' : 'transparent', transition: 'all 0.15s' }}>
                    {ct.icon}
                  </button>
                ))}
              </div>
              <button onClick={() => setOpen(false)}
                style={{ width: 26, height: 26, borderRadius: 6, border: '1px solid #fed7aa', background: '#fff', color: '#ea580c', cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>
                ✕
              </button>
            </div>
          </div>

          {/* Chart */}
          <div style={{ padding: '10px 8px 4px' }}>
            {renderChart()}
          </div>

          {/* Footer switcher */}
          <div style={{ padding: '6px 14px 10px', display: 'flex', gap: 6 }}>
            {relevantTypes.map(ct => (
              <button key={ct.id} onClick={() => setActiveType(ct.id)}
                style={{ padding: '3px 10px', borderRadius: 20, border: `1px solid ${activeType === ct.id ? '#f97316' : '#e2e8f0'}`, background: activeType === ct.id ? 'rgba(249,115,22,0.08)' : '#fff', color: activeType === ct.id ? '#f97316' : '#64748b', fontSize: 10, fontWeight: 700, cursor: 'pointer' }}>
                {ct.icon} {ct.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}